import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Shield } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNotification } from "../../context/NotificationContext";
import client from "../../api/client";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import TermsAcceptance, {
  allTermsAccepted,
  validateTermsAcceptance,
} from "../../components/legal/TermsAcceptance";
import { glassCard } from "../../utils/glassStyles";

export default function AcceptPolicies() {
  const { user, updateUser, loading: authLoading } = useAuth();
  const { add } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const [values, setValues] = useState({
    acceptTerms: false,
    acceptPrivacy: false,
    acceptCookies: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key, checked) => {
    setValues((prev) => ({ ...prev, [key]: checked }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateTermsAcceptance(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      await client.post("/auth/accept-policies", values);
      updateUser({ termsAccepted: true });
      add("Thank you for accepting our policies.", "success");
      const redirectTo = location.state?.from || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      add(err.response?.data?.message || "Could not save your acceptance", "error");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  if (user?.termsAccepted) {
    navigate("/", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className={`w-full max-w-lg ${glassCard}`}>
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-accent">
            <Shield className="h-7 w-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Accept our policies</CardTitle>
          <CardDescription>
            Before you continue, please review and accept our legal agreements.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <TermsAcceptance values={values} onChange={handleChange} errors={errors} />
            <Button type="submit" className="w-full" disabled={submitting || !allTermsAccepted(values)}>
              {submitting ? "Saving..." : "Continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
